import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Stack,
  Title,
  Group,
  Badge,
  Card,
  Grid,
  List as MantineList,
  ThemeIcon,
  LoadingOverlay,
} from '@mantine/core';
import {
  type ClientStatistics,
  type ClientPersonalStatistics,
} from '@/schemas/API/POST/statistics/clients/response';
import { PieChart, BarChart } from '@mantine/charts';
import { List } from '../List/List';
import { TextHeader } from '../List/Header/TextHeader';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { colors } from '@/utils/colors';
import {
  useStatistics,
  type PagingOptions,
} from '@/hooks/statistics/useStatistics';
import { type Filter } from '@/schemas/API/POST/statistics/request';
import { NoDataStatistics } from './NoDataStatistics';

interface Props {
  filter: Filter;
}

const genderMap = new Map<string, string>([
  ['FEMALE', 'žena'],
  ['MALE', 'muž'],
  ['OTHER', 'jiné'],
]);

const defaultClientStatistics: ClientStatistics = {
  clients: {
    content: [],
    pageable: {
      paged: true,
      pageNumber: 0,
      pageSize: 10,
      offset: '0',
      unpaged: false,
      sort: {
        empty: true,
        sorted: false,
        unsorted: true,
      },
    },
    empty: 0,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    size: 20,
    sort: {
      empty: true,
      sorted: false,
      unsorted: true,
    },
    totalElements: 0,
    totalPages: 0,
  },
  priceAmountByService: [],
  ages: [],
  genders: [],
  minutesSpentByTag: [],
};

const NameCell = (client: ClientPersonalStatistics) => (
  <Text key={client.id}>{client.name}</Text>
);

const EmailCell = (client: ClientPersonalStatistics) => (
  <Text key={client.id}>{client.email}</Text>
);

const PriceAmountCell = (client: ClientPersonalStatistics) => (
  <Text key={client.id}>{client.priceAmount} Kč</Text>
);

export const ClientStatisticsComponent = ({ filter }: Props): JSX.Element => {
  const navigate = useNavigate();

  const [requestParams, setRequestParams] = useState<PagingOptions>({
    page: 0,
    size: 10,
    sort: [],
  });

  const {
    data = defaultClientStatistics,
    error,
    isLoading,
  } = useStatistics<ClientStatistics>('clients', filter, requestParams);

  if (data.clients.empty && !isLoading) {
    // needs to be executed before the others, otherwise permanent loading
    return <NoDataStatistics />;
  }

  if (error) {
    return <Text>Chyba při načítání dat.</Text>;
  }

  const onPageChange = (pageNumber: number) => {
    setRequestParams({
      ...requestParams,
      page: pageNumber,
    });
  };

  const totalSpend = data.priceAmountByService.reduce(
    (sum, e) => sum + e.amount,
    0
  );

  const maxAgeAmount = Math.max(...data.ages.map(a => a.amount));
  let ticks: number[] = [];

  if (maxAgeAmount <= 5) {
    ticks = Array.from({ length: maxAgeAmount + 1 }, (_, i) => i);
  } else {
    const step = Math.ceil(maxAgeAmount / 5);
    ticks = Array.from(
      { length: Math.floor(maxAgeAmount / step) + 1 },
      (_, i) => i * step
    );

    if (ticks[ticks.length - 1] !== maxAgeAmount) {
      ticks.push(step * 5);
    }
  }

  const domain: [number, number] = [0, ticks[ticks.length - 1]];

  return (
    <Stack gap="lg" align="flex-start">
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ color: 'blue', type: 'bars' }}
      />
      <Grid grow justify="center" align="stretch">
        <Grid.Col span={4}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Group
              justify="space-evenly"
              className="w-full h-full rounded-card"
            >
              <Stack align="center" justify="flex-start">
                <Title order={5}>Tagy na úkolech</Title>
                <PieChart
                  data={data.minutesSpentByTag.map((item, index) => ({
                    name: item.name,
                    value: item.amount / 60,
                    color: colors[index % colors.length],
                  }))}
                  tooltipDataSource="segment"
                  withTooltip
                  tooltipAnimationDuration={200}
                  strokeWidth={2}
                  size={200}
                />
              </Stack>

              <MantineList spacing="xs" size="sm" withPadding>
                {data.minutesSpentByTag.map((item, index) => (
                  <MantineList.Item
                    key={item.name}
                    icon={
                      <ThemeIcon
                        color={colors[index % colors.length]}
                        radius="xl"
                        size={10}
                      />
                    }
                  >
                    {item.name} ({Math.round(item.amount / 60)} h)
                  </MantineList.Item>
                ))}
              </MantineList>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={6}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Stack>
              <Title order={5}>Rozdělení podle věku</Title>
              <BarChart
                h={200}
                data={data.ages.map(a => ({
                  name: a.name,
                  počet: a.amount,
                }))}
                dataKey="name"
                series={[{ name: 'počet', color: 'blue.6' }]}
                xAxisProps={{
                  domain,
                  ticks,
                }}
                tooltipAnimationDuration={200}
                orientation="vertical"
              />
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={4}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-evenly" className="w-full">
              <Stack align="center" justify="flex-start">
                <Title order={5}>Rozdělení podle pohlaví</Title>
                <PieChart
                  data={data.genders.map((item, index) => ({
                    name: genderMap.get(item.name) ?? item.name,
                    value: item.amount,
                    color: colors[index % colors.length],
                  }))}
                  tooltipDataSource="segment"
                  withTooltip
                  tooltipAnimationDuration={200}
                  strokeWidth={2}
                  size={200}
                />
              </Stack>
              <MantineList spacing="xs" size="sm" withPadding>
                {data.genders.map((item, index) => (
                  <MantineList.Item
                    key={item.name}
                    icon={
                      <ThemeIcon
                        color={colors[index % colors.length]}
                        radius="xl"
                        size={10}
                      />
                    }
                  >
                    {genderMap.get(item.name) ?? item.name} ({item.amount} osob)
                  </MantineList.Item>
                ))}
              </MantineList>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={6}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Stack>
              <Title order={5}>Útrata na jednotlivých službách</Title>
              <BarChart
                h={200}
                data={data.priceAmountByService.map(s => ({
                  name: s.name,
                  Kč: s.amount,
                }))}
                dataKey="name"
                series={[{ name: 'Kč', color: 'teal.6' }]}
              />
              <Text fz="sm">
                Celkem: <Badge>{totalSpend} Kč</Badge>
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <div className="h-full w-full overflow-auto">
        <List
          loading={!data}
          pagination={{
            page: Number(data.clients.pageable.pageNumber + 1),
            setPage: page => onPageChange(page),
            pageTotal: data.clients.totalPages ?? 1,
          }}
          perPageSelector={{
            label: 'Klientů na stránce',
            value: requestParams.size ?? 0,
            setter: v =>
              setRequestParams(currentParams => ({
                ...currentParams,
                size: v,
              })),
          }}
          columns={[
            <SortHeader
              label="Jméno"
              key="jmeno"
              fields={['name']}
              callback={sort => {
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
            <TextHeader key="email">E-mail</TextHeader>,
            <SortHeader
              label="Výdělek"
              key="priceAmount"
              fields={['priceAmount']}
              callback={sort => {
                const newSort = sort.length ? sort : ['priceAmount,asc'];
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort: newSort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
          ]}
          content={{
            items: data?.clients.content ?? [],
            onClick: (client: ClientPersonalStatistics) =>
              navigate(`/clients/${client.id}/personal`),
            renders: [NameCell, EmailCell, PriceAmountCell],
          }}
        />
      </div>
      {data.clients.pageable.pageNumber}
    </Stack>
  );
};
